import React, { useState, useEffect, useRef } from "react";
import {
    Row,
    Col,
    Button
} from "../../ui/ot";
import { Outlet, Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useWebsiteBasicInformation } from "../controller/WebsiteBasicInformationController";
import axios from "axios";
import { ChevronDownIcon } from '@radix-ui/react-icons';

// Define common colors
const colors = {
    mainColor: '#d56b6b',
    lightColor: '#f9f9f9',
    darkColor: '#333333',
    darkBg: '#eeeeee',
    lightText: '#555555',
    lightBorder: '#f3b7b7',
    borderColor: '#d56b6b',
};

// Header and Footer styles
const HeaderContainer = styled.header`
    background: ${colors.lightColor};
    color: ${colors.mainColor};
    padding: 1rem 2rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1); // Reduced shadow opacity
    position: relative;
    z-index: 10;

    @media (max-width: 768px) {
        flex-direction: column;
        padding: 1rem;
    }
`;

const FooterContainer = styled.footer`
    background-color: ${colors.lightColor};
    color: ${colors.mainColor};
    padding: 2rem 1.5rem;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.1); // Reduced shadow opacity

    @media (max-width: 768px) {
        padding: 1rem;
        flex-direction: column;
        align-items: center;
    }
`;

const FooterContent = styled.div`
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: space-between;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: center;
    }
`;

const FooterSection = styled.div`
    flex: 1;
    padding: 10px;

    @media (max-width: 768px) {
        text-align: center;
    }
`;

// Navbar styles
const StyledNavMenu = styled.nav`
    display: flex;
    align-items: center;
    background: ${colors.lightColor};
    padding: 0.5rem;
    position: relative;
    z-index: 10;

    @media (max-width: 768px) {
        padding: 0;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: ${colors.mainColor};
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 20;

    @media (min-width: 769px) {
        display: none;
    }

    &:focus {
        outline: none;
    }

    &:hover {
        color: ${colors.darkColor};
    }
`;

const MenuToggle = styled.button`
    background: ${colors.mainColor};
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: ${colors.lightColor};
    display: block;
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    padding: 0.5rem;
    border-radius: 4px;
    transition: background 0.3s ease;

    @media (min-width: 769px) {
        display: none;
    }

    &:focus {
        outline: none;
    }

    &:hover {
        background: ${colors.darkColor};
        color: ${colors.lightColor};
    }
`;

const StyledNavContent = styled.ul`
    background: ${colors.darkBg};
    position: absolute;
    top: ${({ level }) => (level === 0 ? '100%' : '0')};
    left: ${({ level }) => (level === 0 ? '0' : '100%')};
    width: auto; /* Match width of StyledNavItem */
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    border-radius: 8px;
    padding: 0;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.3s ease, visibility 0.3s ease;
    z-index: 15;

    @media (max-width: 768px) {
        width: 100%; /* Full width on mobile */
        max-width: none; /* Remove max-width restriction */
    }
`;

const StyledNavTrigger = styled.button`
    background: none;
    border: none;
    color: ${colors.mainColor};
    font-size: 1rem;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 4px;
    display: flex;
    align-items: center;
    transition: background 0.3s ease;
    white-space: nowrap;
    font-weight: bold;
    width: 100%; /* Ensures the button fills its parent container */
    text-align: center;

    &:hover {
        background: ${colors.lightBorder};
    }

    @media (max-width: 768px) {
        font-size: 1.25rem;
        width: 100%; /* Ensures full width on mobile */
    }
`;

const StyledNavItem = styled.li`
    position: relative;
    display: flex;
    align-items: center;
    background: ${({ isActive }) => (isActive ? colors.lightBorder : 'transparent')};
    color: ${colors.mainColor};
    cursor: pointer;
    transition: background 0.3s ease;
    width: 100%; /* Ensures the item container is full width */
    text-align: center;
    font-weight: bold;
    padding: 0.5rem; /* Consistent padding */

    &:last-child {
        border-bottom: none;
    }

    &:hover > ${StyledNavContent} {
        opacity: 1;
        visibility: visible;
    }

    @media (max-width: 768px) {
        width: 100%; /* Ensures full width on mobile */
        padding: 1rem; /* Adjust padding for mobile */
    }
`;

const StyledNavList = styled.ul`
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 0.5rem; /* Reduce gap between items */
    
    @media (max-width: 768px) {
        display: ${({ open }) => (open ? 'flex' : 'none')};
        flex-direction: column;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100vh;
        background: ${colors.darkBg};
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        padding: 1rem;
        transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
        transform: ${({ open }) => (open ? 'translateX(0)' : 'translateX(-100%)')};
        opacity: ${({ open }) => (open ? '1' : '0')};
        overflow-y: auto;
        z-index: 10;
    }
`;

const Logo = styled.div`
    display: flex;
    align-items: center;
    font-size: 1.5rem;
    font-weight: bold;
    color: ${colors.mainColor};

    img {
        height: 50px;
        margin-right: 1rem; // Add margin for spacing
    }

    @media (max-width: 768px) {
        font-size: 1.25rem;
    }
`;

const ContentContainer = styled.main`
    padding: 2rem;
    background-color: ${colors.lightColor};
    min-height: calc(100vh - 220px);

    @media (max-width: 768px) {
        padding: 1rem;
    }
`;

const ContactInfo = styled.div`
    margin-bottom: 1rem;
    font-size: 16px;
    line-height: 1.5;
`;

const ContactInfoItem = styled.p`
    margin: 0.5rem 0;
    font-size: 16px;

    strong {
        font-weight: bold;
    }

    a {
        color: ${colors.mainColor};
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const ContactButtons = styled.div`
    display: flex;
    gap: 10px;
    justify-content: center;
`;

const ContactUsButton = styled.button`
    padding: 10px 20px;
    font-size: 16px;
    color: ${colors.lightColor};
    background-color: ${colors.mainColor};
    border: none;
    border-radius: 5px;
    cursor: pointer;
    transition: background-color 0.3s;

    &:hover {
        background-color: ${colors.darkColor};
    }
`;

const ReportIssueButton = styled(ContactUsButton)`
    color: ${colors.mainColor}; 
    background-color: ${colors.darkColor}; 

    &:hover {
        background-color: ${colors.mainColor}; 
        color: ${colors.darkColor};
    }
`;

const SocialMediaIcons = styled.div`
    display: flex;
    justify-content: center; /* Centers items horizontally */
    gap: 15px;
    margin-top: 1rem;

    a {
        color: ${colors.mainColor};
        font-size: 24px;
        transition: color 0.3s ease;

        &:hover {
            color: ${colors.darkColor};
        }
    }
`;

// Recursive Menu Component
const MenuItem = ({ item, level = 0, isActive, handleToggle = () => { } }) => {
    return (
        <StyledNavItem key={item.key} isActive={isActive}>
            {
                item.path_check === 0
                    ?
                    <Link to={item.key} style={{ textDecoration: 'none' }} onClick={() => handleToggle()}>
                        <StyledNavTrigger>
                            {item.label}
                            {item.children && item.children.length > 0 && (
                                <ChevronDownIcon style={{ marginLeft: '8px' }} />
                            )}
                        </StyledNavTrigger>
                    </Link>
                    : <a href={item.key} style={{ textDecoration: 'none', color: 'black' }} target="_blank">
                        <StyledNavTrigger>
                            {item.label}
                            {item.children && item.children.length > 0 && (
                                <ChevronDownIcon style={{ marginLeft: '8px' }} />
                            )}
                        </StyledNavTrigger>
                    </a>
            }
            {item.children && item.children.length > 0 && (
                <StyledNavContent level={level}>
                    {item.children.map((child) => (
                        <MenuItem item={child} level={level + 1} key={child.key} path_check={child.path_check} handleToggle={handleToggle} />
                    ))}
                </StyledNavContent>
            )}
        </StyledNavItem>
    );
};

// Navbar Component
const Navbar = React.memo(({ information, menuData, imageFileData = [] }) => {
    const [openItem, setOpenItem] = useState(false);

    const handleToggle = () => {
        setOpenItem(!openItem);
    };

    const renderLogoImage = (imageFileDatas) => {
        let LogoImageFileData = imageFileDatas?.find(ele => ele?.title === "Logo");
        let classifyStructureTypeFileData = LogoImageFileData?.classify_structure_type_file_data || [];
        if (classifyStructureTypeFileData.length > 0) {
            return classifyStructureTypeFileData.map((file) => {
                return (
                    <a href="/" key={file.file_id}><img
                        style={{ width: "auto", height: "50px" }}
                        src={file?.file_url}
                        alt="Logo"
                    />
                    </a>
                );
            });
        } else {
            let nameLanguageData = information?.name_language_data || [];
            let showNameData = nameLanguageData.find((ele) => ele?.language_culture_code === "zh-tw");
            let showName = showNameData?.language_culture_value;
            return showName || "Logo";
        }
    };

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth > 768) {
                setOpenItem(false);
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <>
            <Logo>{renderLogoImage(imageFileData)}</Logo>
            <MenuToggle onClick={handleToggle}>
                {openItem ? '✕' : '☰'}
            </MenuToggle>
            <StyledNavMenu open={openItem}>
                <StyledNavList open={openItem}>
                    {openItem && (
                        <CloseButton onClick={handleToggle}>
                            ✖
                        </CloseButton>
                    )}
                    {menuData.map((item) => (
                        <MenuItem item={item} key={item.key} path_check={item.path_check} handleToggle={handleToggle} />
                    ))}
                </StyledNavList>
            </StyledNavMenu>
        </>
    );
});

const Footer = ({ information = [], imageFileData = [] }) => {
    const navigate = useNavigate();

    const handleContactUs = () => {
        navigate('/officialweb/frontend/appointment');
    };

    const handleReportIssue = () => {
        navigate('/officialweb/frontend/question_and_answer');
    };

    const renderLogoImage = (imageFileDatas) => {
        let LogoImageFileData = imageFileDatas?.find(ele => ele?.title === "頁尾Logo");
        let classifyStructureTypeFileData = LogoImageFileData?.classify_structure_type_file_data || [];
        if (classifyStructureTypeFileData.length > 0) {
            return classifyStructureTypeFileData.map((file) => {
                return (
                    <img
                        key={file.file_id}
                        style={{ width: "auto", height: "50px" }}
                        src={file?.file_url}
                        alt="Footer Logo"
                    />
                );
            });
        } else {
            let nameLanguageData = information?.name_language_data || [];
            let showNameData = nameLanguageData.find((ele) => ele?.language_culture_code === "zh-tw");
            let showName = showNameData?.language_culture_value;
            return showName || "Logo";
        }
    };

    return (
        <FooterContainer>
            <FooterContent>
                <FooterSection>
                    {renderLogoImage(imageFileData)}
                    <h3>
                        <span>{renderLogoImage([])}© 2024</span>
                    </h3>
                    <SocialMediaIcons>
                        {(information?.information_community_data || []).map(
                            (communityDataItem, communityDataIndex) => (
                                <a key={communityDataIndex} href={communityDataItem?.link} target="_blank" rel="noopener noreferrer">
                                    <img
                                        style={{ height: "30px" }}
                                        src={communityDataItem?.icon}
                                        alt={`${communityDataItem?.link} Icon`}
                                    />
                                </a>
                            )
                        )}
                    </SocialMediaIcons>
                </FooterSection>

                <FooterSection>
                    <ContactInfo>
                        <ContactInfoItem>
                            <strong>地址：</strong>{information?.address}
                        </ContactInfoItem>
                        <ContactInfoItem>
                            <strong>電話：</strong>{information?.phone}
                        </ContactInfoItem>
                        <ContactInfoItem>
                            <strong>電子郵件：</strong> {information?.email}
                        </ContactInfoItem>
                    </ContactInfo>

                    <ContactButtons>
                        <ContactUsButton onClick={handleContactUs}>聯絡我們</ContactUsButton>
                        <ReportIssueButton onClick={handleReportIssue}>網站問題</ReportIssueButton>
                    </ContactButtons>
                </FooterSection>

                <FooterSection>
                    <div
                        dangerouslySetInnerHTML={{
                            __html: information?.google_map_iframe?.replace(
                                `width="600" height="450"`,
                                `width="100%" height="100%"`
                            ),
                        }}
                    />
                </FooterSection>
            </FooterContent>
        </FooterContainer>
    );
};

const Content = ({ children }) => (
    <ContentContainer>{children}</ContentContainer>
);

const Layout = ({ admin = false, menuData = [] }) => {
    const navigate = useNavigate();

    const [information, setInformation] = useState(null);
    const [imageFile, setImageFile] = useState([]);
    const firstRender = useRef(true);

    const get_picture = (temp) => {
        (temp || []).forEach((element) => {
            const fileDataArray = element.classify_structure_type_file_data || [];
            fileDataArray.forEach((fileData) => {
                if (fileData?.file_id) {
                    axios
                        .get("/media/api/file/upload", {
                            responseType: "blob",
                            params: {
                                file_id: fileData?.file_id,
                            },
                        })
                        .then((response) => {
                            var blob_ = new Blob([response.data], {
                                type: "image/jpg", // 如果是 SVG, 這裡可以改成 image/svg+xml
                            });
                            const dataurl = URL.createObjectURL(blob_);

                            setImageFile((pre) => {
                                return pre.map((element) => {
                                    if (element.classify_structure_type_file_data) {
                                        element.classify_structure_type_file_data = element.classify_structure_type_file_data.map((file) => {
                                            if (parseInt(fileData?.file_id) === parseInt(file?.file_id)) {
                                                return {
                                                    ...file,
                                                    file_url: dataurl,
                                                };
                                            } else {
                                                return file;
                                            }
                                        });
                                    }
                                    return element;
                                });
                            });
                        })
                        .catch((error) => {
                            console.error(`Error fetching image for file_id ${fileData?.file_id}:`, error);
                        });
                }
            });
        });
    };

    const {
        WebsiteBasicInformationData,
        getWebsiteBasicInformationData,
    } = useWebsiteBasicInformation({});

    useEffect(() => {
        console.log('WebsiteBasicInformationData')
        console.log(WebsiteBasicInformationData)
        let WebsiteBasicInformationDataFirst = WebsiteBasicInformationData?.find(_ => true);
        let classifyStructureTypeData = WebsiteBasicInformationDataFirst?.classify_structure_type_data || [];
        setImageFile(classifyStructureTypeData);
        setInformation(WebsiteBasicInformationDataFirst);
    }, [WebsiteBasicInformationData]);

    useEffect(() => {
        getWebsiteBasicInformationData();
    }, []);

    useEffect(() => {
        if (firstRender.current && imageFile.length > 0) {
            get_picture(imageFile);
            firstRender.current = false;
        }
    }, [imageFile]);

    return (
        <>
            <HeaderContainer>
                <Navbar
                    information={information}
                    imageFileData={imageFile}
                    menuData={menuData}
                />
            </HeaderContainer>
            <Content>
                <Row gutter={[0, 8]}>

                    {admin ? (
                        <>
                            <Col span={24}>
                                <Row justify={"end"}>
                                    <Col>
                                        <Button
                                            type="primary"
                                            className="example"
                                            onClick={(e) => window.location.href = "/officialWeb/admin"}
                                        >
                                            回到後台
                                        </Button>
                                    </Col>
                                </Row>
                            </Col>
                        </>
                    ) : null}
                    <Col span={24}>
                        <Outlet />
                    </Col>
                </Row>
            </Content>
            <Footer
                information={information}
                imageFileData={imageFile}
            />
        </>
    );
};

export default Layout;
